import { useId, useState, type ReactNode } from 'react';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../components/ui/tooltip';

interface ReviewCellMessageProps {
  /** Undefined when the cell has nothing to say. */
  message: string | undefined;
  /** The cell's focusable control (its button or input). */
  children: ReactNode;
}

/**
 * A review-grid cell's message — a validation error, or the value the
 * campaign holds today. A native `title` only reaches mouse users; this shows
 * the message on hover AND keyboard focus, and names it as the control's
 * description so screen readers announce it.
 *
 * The Tooltip tree stays mounted with or without a message: swapping it in
 * and out would remount the control, and an input would lose focus the moment
 * its error clears mid-typing. Same constant-tree pattern as TruncationTooltip.
 */
export function ReviewCellMessage({ message, children }: ReviewCellMessageProps) {
  const messageId = useId();
  const [open, setOpen] = useState(false);
  const hasMessage = message !== undefined;
  const describedBy = hasMessage ? messageId : undefined;
  return (
    <>
      <Tooltip open={hasMessage && open} onOpenChange={setOpen}>
        <TooltipTrigger asChild aria-describedby={describedBy}>
          {children}
        </TooltipTrigger>
        {hasMessage && <TooltipContent>{message}</TooltipContent>}
      </Tooltip>
      {hasMessage && (
        <span id={messageId} className="sr-only">
          {message}
        </span>
      )}
    </>
  );
}
